import React, { useState } from 'react';
import { MapPin, Phone, Mail, MessageCircle, Copy, Check, QrCode } from 'lucide-react';
import Breadcrumbs from './Navigation/Breadcrumbs';
import RequestForm from './services/RequestForm';

function ContactsPage() {
  const [showQRCode, setShowQRCode] = useState(false);

  const warehouses = {
    guangzhou: {
      contact: '阿泰17310189679',
      address: '广州省广州市白云区石沙路289号正达产业园11栋105号997库房'
    },
    yiwu: {
      contact: '阿泰17310189679',
      address: '浙江省义乌市陶界岭小区75栋997库房'
    }
  };

  const [copiedGuangzhou, setCopiedGuangzhou] = useState(false);
  const [copiedYiwu, setCopiedYiwu] = useState(false);

  const handleCopy = async (text: string, type: 'guangzhou' | 'yiwu') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'guangzhou') {
        setCopiedGuangzhou(true);
        setTimeout(() => setCopiedGuangzhou(false), 2000);
      } else {
        setCopiedYiwu(true);
        setTimeout(() => setCopiedYiwu(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto">
          <div className="max-w-[780px] relative">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Контакты</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF] animate-gradient-wave">
                для связи с нами
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Выберите удобный способ связи с нашей командой
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            {/* Warehouses Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-[#1B3BA0] mb-6 flex items-center">
                <MapPin className="w-6 h-6 text-[#1B3BA0] mr-2" />
                Адреса складов
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Guangzhou Warehouse */}
                <div className="bg-[#1B3BA0]/5 rounded-2xl p-6 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                  <h3 className="text-lg font-bold text-[#1B3BA0] mb-4">Склад Гуанчжоу</h3>
                  <div className="mb-4">
                    <div className="bg-white rounded-xl p-4 mb-3 shadow-md">
                      <p className="text-gray-600 text-sm mb-1">Контакт:</p>
                      <p className="text-gray-800 font-medium mb-3">{warehouses.guangzhou.contact}</p>
                      <p className="text-gray-600 text-sm mb-1">Адрес:</p>
                      <p className="text-gray-800 font-medium">{warehouses.guangzhou.address}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(`${warehouses.guangzhou.contact}\n${warehouses.guangzhou.address}`, 'guangzhou')}
                      className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#1B3BA0] hover:bg-[#1B3BA0] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      {copiedGuangzhou ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copiedGuangzhou ? 'Скопировано!' : 'Скопировать'}
                    </button>
                  </div>
                </div>

                {/* Yiwu Warehouse */}
                <div className="bg-[#1B3BA0]/5 rounded-2xl p-6 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                  <h3 className="text-lg font-bold text-[#1B3BA0] mb-4">Склад Иу</h3>
                  <div className="mb-4">
                    <div className="bg-white rounded-xl p-4 mb-3 shadow-md">
                      <p className="text-gray-600 text-sm mb-1">Контакт:</p>
                      <p className="text-gray-800 font-medium mb-3">{warehouses.yiwu.contact}</p>
                      <p className="text-gray-600 text-sm mb-1">Адрес:</p>
                      <p className="text-gray-800 font-medium">{warehouses.yiwu.address}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(`${warehouses.yiwu.contact}\n${warehouses.yiwu.address}`, 'yiwu')}
                      className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#1B3BA0] hover:bg-[#1B3BA0] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      {copiedYiwu ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copiedYiwu ? 'Скопировано!' : 'Скопировать'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Phone & Email Card */}
            <div className="space-y-8">
              {/* Phone */}
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                <h2 className="text-xl font-bold text-[#1B3BA0] mb-4 flex items-center">
                  <Phone className="w-6 h-6 text-[#1B3BA0] mr-2" />
                  Телефон
                </h2>
                <a 
                  href="tel:+8617310189679"
                  className="text-xl text-gray-800 block mb-2 hover:text-[#1B3BA0] transition-colors"
                >
                  +86 173 1018-96-79

                </a>
                <p className="text-gray-600">Пн–Вс: 9:00 — 18:00</p>
              </div>

              {/* Email */}
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                <h2 className="text-xl font-bold text-[#1B3BA0] mb-4 flex items-center">
                  <Mail className="w-6 h-6 text-[#1B3BA0] mr-2" />
                  Email
                </h2>
                <a 
                  href="mailto:cargo997@mail.ru"
                  className="text-xl text-gray-800 hover:text-[#1B3BA0] transition-colors"
                >
                  cargo997@mail.ru
                </a>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Messengers */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Связаться в мессенджерах
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Telegram */}
            <a
              href="https://t.me/A997Cargo"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">Telegram</h3>
              <p className="text-gray-600">Открыть чат</p>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/8617310189679"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">WhatsApp</h3>
              <p className="text-gray-600">Написать в WhatsApp</p>
            </a>

            {/* WeChat */}
            <button
              onClick={() => setShowQRCode(true)}
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">WeChat</h3>
              <p className="text-gray-600">Показать QR-код</p>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Остались вопросы?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Заполните форму, и мы свяжемся с вами в ближайшее время
          </p>
          <RequestForm source="Контакты" />
        </div>
      </section>

      {/* WeChat QR Code Modal */}
      {showQRCode && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          onClick={() => setShowQRCode(false)}
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-[#1B3BA0] mb-6">
              Отсканируйте QR-код в WeChat
            </h3>
            <img
              src="https://i.ibb.co/3yRhwx6w/b284c5168ba8cccd3b21edba31d2f2e4.jpg"
              alt="WeChat QR"
              className="w-48 h-48 object-cover rounded-xl shadow-xl mx-auto mb-6"
            />
            <button
              onClick={() => setShowQRCode(false)}
              className="w-full bg-[#1B3BA0] text-white py-3 rounded-xl hover:bg-[#0F2875] transition-colors font-medium shadow-md hover:shadow-lg"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes gradient-wave {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-wave {
          animation: gradient-wave 6s ease-in-out infinite;
          background-size: 200% auto;
        }
      `}</style>
    </div>
  );
}

export default ContactsPage;